/**
 * Formula Validator (ID-based token system)
 * 
 * Validates board spec formulas using the deterministic lexer from formulaParser.
 * No regex is used for tokenization — all token boundaries are determined by
 * the character-by-character lexer.
 *
 * Mirror of backend/src/lib/formulaValidator.js
 */

import {
    CATEGORIES,
    ALL_CATEGORIES,
    OPERATORS,
    isJournalToken,
    isCategory,
    isAllKeyword,
} from './formulaTokens';

import { lex, TokenType } from './formulaParser';

// Re-export for backward compatibility
export { CATEGORIES, ALL_CATEGORIES, OPERATORS };

export interface FormulaPart {
    type: 'variable' | 'number' | 'operator';
    value: string;
    isValid?: boolean;
    isSoft?: boolean;
    error?: ValidationError;
}

export interface ValidationError {
    key: string;
    params?: Record<string, any>;
    message?: string;
}

export interface ValidationResult {
    isValid: boolean;
    errors: ValidationError[];
    parts: FormulaPart[];
}

export interface ValidationOptions {
    context?: {
        /** Valid root tokens (channel/journal/bridge-entity IDs or 'all') */
        roots: string[];
        /** Map of parent token -> array of child tokens */
        childrenOf: Record<string, string[]>;
    };
    isTyping?: boolean;
}

/**
 * Validates a formula string with ID-based tokens.
 * Uses the parser's lexer for tokenization instead of regex.
 */
export function validateFormula(formula: string, options: ValidationOptions = {}): ValidationResult {
    const { context = null, isTyping = false } = options;
    const cleanFormula = formula.replace(/\s+/g, '');

    if (!cleanFormula && !isTyping) {
        return { isValid: false, errors: [{ key: 'boardSpecs.errors.empty' }], parts: [] };
    }

    if (!cleanFormula) {
        return { isValid: true, errors: [], parts: [] };
    }

    const errors: ValidationError[] = [];
    const parts: FormulaPart[] = [];

    // Use the deterministic lexer instead of regex
    const { tokens, errors: lexErrors } = lex(cleanFormula);

    // Report lex errors — but trailing dot errors are soft when typing
    lexErrors.forEach(e => {
        const isTrailingDot = e.includes("Unexpected '.'") && cleanFormula.trim().endsWith('.');
        if (isTrailingDot && isTyping) {
            // Suppress trailing dot errors while typing (soft error)
        } else {
            errors.push({ key: 'boardSpecs.errors.invalidChar', params: { message: e } });
        }
    });

    let lastPart: FormulaPart | null = null;

    // Process tokens (the last token is always EOF)
    for (let i = 0; i < tokens.length; i++) {
        const tok = tokens[i];

        if (tok.type === TokenType.EOF) break;

        const isLastToken = (i === tokens.length - 2); // before EOF

        if (tok.type === TokenType.OPERATOR) {
            const operator = tok.value;
            const isLast = isLastToken;
            const isSoft = isLast && isTyping && operator !== ')';
            const isMismatched = lastPart && lastPart.type === 'operator' &&
                lastPart.value !== ')' && operator !== '(' && operator !== '-';

            const isValid = !isMismatched && (!isLast || operator === ')');
            const errorKey = isMismatched ? 'boardSpecs.errors.unexpectedOperator' : (isLast && operator !== ')' ? 'boardSpecs.errors.endsInOperator' : undefined);
            const error = errorKey ? { key: errorKey } : undefined;

            parts.push({
                type: 'operator',
                value: operator,
                isValid: isValid || isSoft,
                isSoft: isSoft,
                error
            });
            if (!isValid && !isSoft && errorKey) errors.push({ key: errorKey });
            lastPart = parts[parts.length - 1];
            continue;
        }

        if (tok.type === TokenType.LPAREN) {
            parts.push({ type: 'operator', value: '(', isValid: true, isSoft: false });
            lastPart = parts[parts.length - 1];
            continue;
        }

        if (tok.type === TokenType.RPAREN) {
            parts.push({ type: 'operator', value: ')', isValid: true, isSoft: false });
            lastPart = parts[parts.length - 1];
            continue;
        }

        if (tok.type === TokenType.NUMBER) {
            const numStr = tok.value;
            const dotCount = (numStr.match(/\./g) || []).length;
            const hasMultipleDots = dotCount > 1;
            const endsInDot = numStr.endsWith('.');

            const isValidNumber = !hasMultipleDots && (!endsInDot || isTyping);
            const isSoft = !hasMultipleDots && endsInDot && isTyping;
            const errorKey = hasMultipleDots ? 'boardSpecs.errors.invalidNumber' : (endsInDot && !isTyping ? 'boardSpecs.errors.numberEndsInDot' : undefined);
            const error = errorKey ? { key: errorKey, params: hasMultipleDots ? { value: numStr } : undefined } : undefined;

            parts.push({ type: 'number', value: numStr, isValid: isValidNumber, isSoft, error });
            if (!isValidNumber && errorKey && !isSoft) errors.push({ key: errorKey });
            lastPart = parts[parts.length - 1];
            continue;
        }

        if (tok.type === TokenType.VARIABLE) {
            const variable = tok.value;
            const varResult = validateVariablePart(variable, context, isTyping);
            parts.push({
                type: 'variable',
                value: variable,
                isValid: varResult.isValid,
                isSoft: varResult.isSoft,
                error: !varResult.isValid && varResult.error ? { key: varResult.error, params: varResult.params } : undefined
            });

            if (!varResult.isValid && varResult.error) {
                if (!varResult.isSoft || !isTyping) {
                    errors.push({ key: varResult.error, params: varResult.params });
                }
            }
            lastPart = parts[parts.length - 1];
            continue;
        }

        errors.push({ key: 'boardSpecs.errors.invalidChar', params: { pos: tok.start } });
    }

    // Trailing dot check
    if (!isTyping && cleanFormula.trim().endsWith('.')) {
        errors.push({ key: 'boardSpecs.errors.endsInDot' });
    }

    // Parenthesis balance check (use the tokens, not regex)
    let parenDepth = 0;
    for (const tok of tokens) {
        if (tok.type === TokenType.LPAREN) parenDepth++;
        if (tok.type === TokenType.RPAREN) parenDepth--;
    }
    if (parenDepth !== 0) {
        errors.push({ key: 'boardSpecs.errors.unbalancedParenthesis' });
    }

    return { isValid: errors.length === 0, errors, parts };
}

/**
 * Validates a single variable part like 'ch_abc.en_def.revenues'
 * 
 * With ID-based tokens, segments are clean: no special characters to worry about.
 */
function validateVariablePart(variable: string, context: ValidationOptions['context'] | null, _isTyping: boolean) {
    const segments = variable.split('.');
    const lastIdx = segments.length - 1;
    const lastSeg = segments[lastIdx].toLowerCase();

    // Double dots
    if (variable.includes('..')) {
        return { isValid: false, isSoft: false, error: 'boardSpecs.errors.doubleDots' };
    }

    // Category must be terminal (can't appear mid-path)
    for (let i = 0; i < lastIdx; i++) {
        if (isCategory(segments[i])) {
            return {
                isValid: false, isSoft: false,
                error: 'boardSpecs.errors.invalidSyntaxTerminal',
                params: { value: segments[i] }
            };
        }
    }

    // Root 'all' special case
    if (isAllKeyword(segments[0])) {
        if (segments.length === 1) {
            return { isValid: false, isSoft: true, error: 'boardSpecs.errors.incompleteAll' };
        }
        if (segments.length === 2) {
            if (isCategory(lastSeg)) return { isValid: true };
            const isMatchPossible = (ALL_CATEGORIES as readonly string[]).some(c => c.startsWith(lastSeg));
            return {
                isValid: false,
                isSoft: isMatchPossible,
                error: isMatchPossible ? 'boardSpecs.errors.incompleteCategory' : 'boardSpecs.errors.invalidCategory',
                params: isMatchPossible ? undefined : { value: lastSeg }
            };
        }
        return { isValid: false, isSoft: false, error: 'boardSpecs.errors.allFollowedByCategory' };
    }

    // Context validation
    if (context) {
        const { roots = [], childrenOf = {} } = context;
        const rootSeg = segments[0]; // ID-based: use as-is (ch_xxx, jn_xxx, en_xxx)

        // Find root
        if (!roots.includes(rootSeg)) {
            // Check if it's a prefix of any root (typing in progress)
            const isMatchPossible = roots.some(r => r.startsWith(rootSeg));
            return {
                isValid: false,
                isSoft: isMatchPossible,
                error: isMatchPossible ? 'boardSpecs.errors.incompleteReference' : 'boardSpecs.errors.unknownConnection',
                params: { value: rootSeg }
            };
        }

        // Traverse child hierarchy
        let currentParent = rootSeg;
        for (let i = 1; i < lastIdx; i++) {
            const seg = segments[i];

            if (isAllKeyword(seg)) {
                // 'all' can only be followed by a category (at lastIdx)
                if (i < lastIdx - 1) {
                    return { isValid: false, isSoft: false, error: 'boardSpecs.errors.onlyCategoryAfterAll' };
                }
                continue;
            }

            const children = childrenOf[currentParent] || [];
            if (!children.includes(seg)) {
                return {
                    isValid: false, isSoft: false,
                    error: 'boardSpecs.errors.itemNotFound',
                    params: { item: seg, parent: currentParent }
                };
            }
            currentParent = seg;
        }

        // Last segment: must be a category
        if (isCategory(lastSeg)) {
            // For journals (jn_ tokens), 2 segments is valid: jn_xxx.revenues
            // For 'all' second-to-last, also valid: ch_xxx.all.revenues
            if (segments.length < 3) {
                const prevSeg = segments.length >= 2 ? segments[segments.length - 2] : '';
                const isJournalRoot = isJournalToken(rootSeg);
                const prevIsAll = isAllKeyword(prevSeg);
                if (!isJournalRoot && !prevIsAll) {
                    return { isValid: false, isSoft: true, error: 'boardSpecs.errors.expectedEntityOrAll' };
                }
            }
            return { isValid: true };
        }

        if (isAllKeyword(lastSeg)) {
            return { isValid: false, isSoft: true, error: 'boardSpecs.errors.incompleteReferenceExpectedCategory' };
        }

        // Is the last segment a known child?
        const children = childrenOf[currentParent] || [];
        const possibleNext = [...children, ...(ALL_CATEGORIES as unknown as string[]), 'all'];
        const isMatchPossible = possibleNext.some(p => p.startsWith(lastSeg));

        if (lastSeg === '') {
            return { isValid: false, isSoft: true, error: 'boardSpecs.errors.incompleteReference' };
        }

        if (children.includes(lastSeg)) {
            return { isValid: false, isSoft: true, error: 'boardSpecs.errors.expectedCategoryAtEnd' };
        }

        // Single-segment root reference
        if (segments.length === 1 && roots.includes(rootSeg)) {
            return { isValid: false, isSoft: true, error: 'boardSpecs.errors.incompleteReference' };
        }

        return {
            isValid: false,
            isSoft: isMatchPossible,
            error: isMatchPossible ? 'boardSpecs.errors.incompleteReference' : 'boardSpecs.errors.invalidItemOrCategory',
            params: isMatchPossible ? undefined : { value: lastSeg }
        };
    }

    // Fallback (no context)
    if (isCategory(lastSeg)) {
        if (segments.length < 3) return { isValid: false, isSoft: true, error: 'boardSpecs.errors.incompleteReference' };
        return { isValid: true };
    }

    const prevSeg = segments[segments.length - 2]?.toLowerCase();
    if (prevSeg === 'all') {
        return { isValid: false, isSoft: true, error: 'boardSpecs.errors.invalidCategory', params: { value: lastSeg } };
    }

    return { isValid: false, isSoft: true, error: 'boardSpecs.errors.expectedCategoryAtEnd' };
}
